import React, { useEffect, useState } from "react";

import { Typography, Button, Grid, Dialog, ButtonBase } from "@mui/material";
import { ExitToAppOutlined } from "@mui/icons-material";
import { updateUser } from "libLegacy/api/user";
import { deleteUser } from "libLegacy/api/dashboard";
import { useLogout, useToken, useUserContext } from "contexts/UserContext";
import { LOGIN } from "routers/routePaths";
import { useSetError } from "contexts/ErrorContext";
import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";
import { TextWithValidation } from "components/MUIExtensions/inputs/TextWithValidation";
import { PasswordText } from "components/MUIExtensions/inputs/PasswordText";
import { CustomCodes } from "contexts/ErrorContextData";
import { AvatarUpload } from "components/MUIExtensions/AvatarUpload";

export const MyAccount = ({ history }) => {
  const token = useToken();
  const logout = useLogout();
  const { userProfile } = useUserContext();
  const [loading, setLoading] = useState(false);
  const setError = useSetError();

  const [name, setName] = useState<string>(userProfile?.name || "");
  const [email, setEmail] = useState<string>(userProfile?.email || "");
  const [phone, setPhone] = useState<string>(userProfile?.phone || "");
  const [company, setCompany] = useState<string>(userProfile?.company || "");
  const [position, setPosition] = useState<string>(userProfile?.position || "");
  const [password, setPassword] = useState<string>("***");
  const [newPassword, setNewPassword] = useState<string>("");
  const [newImg, setNewImg] = useState<HTMLInputElement["files"] | null>(null);

  const [showPasswordUpdate, setShowPasswordUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name || "");
      setEmail(userProfile.email || "");
      setPhone(userProfile.phone || "");
      setCompany(userProfile.company || "");
      setPosition(userProfile.position || "");
      setPassword("***");
    }
  }, [userProfile]);

  const handleSubmit = async () => {
    try {
      await updateUser(`Bearer ${token}`, {
        name,
        phone,
        company,
        position,
        ...(newImg && { profilePicture: newImg[0] }),
        ...(showPasswordUpdate && { newPassword }),
      });
    } catch (error) {
      // @ts-expect-error
      setError(error.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteUser(userProfile?.userId, token);
      logout();
      history.push("/");
    } catch (error) {
      // @ts-expect-error
      setError(error.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    history.push(LOGIN);
  };

  const handlePasswordUpdate = () => {
    setShowPasswordUpdate(false);
  };

  const handleFileUpload = (file) => {
    if (file) {
      setNewImg(file);
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  return (
    <PageStandard title="Minha Conta">
      <Grid item container justifyContent="center" sx={{ mb: 2 }}>
        <AvatarUpload
          fileSrc={userProfile?.profilePictureUrl}
          currentFile={newImg || undefined}
          onFileUpload={handleFileUpload}
        />
      </Grid>
      <BlockStandard hideDivider>
        <Grid container gap={3} direction="column">
          <TextWithValidation
            label="Nome completo"
            value={name}
            onChange={setName}
          />
          <TextWithValidation
            label="E-mail"
            value={email}
            onChange={setEmail}
            type="email"
            helperText="Entre em contato com o suporte para alterar seu e-mail"
            disabled
          />
          <TextWithValidation
            label="Tel"
            value={phone}
            onChange={setPhone}
            type="tel"
          />
          <TextWithValidation
            label="Empresa"
            value={company}
            onChange={setCompany}
          />
          <TextWithValidation
            label="Cargo"
            value={position}
            onChange={setPosition}
          />
          <Grid item>
            <PasswordText
              value={password}
              onChange={setPassword}
              label="Minha senha"
              disabled
            />
          </Grid>
        </Grid>
        <ButtonBase
          onClick={() => setShowPasswordUpdate(true)}
          sx={{ justifyContent: "flex-start" }}
        >
          <Typography variant="caption" sx={{ textDecoration: "underline" }}>
            Alterar senha
          </Typography>
        </ButtonBase>
      </BlockStandard>

      <BlockStandard>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          Salvar alterações
        </Button>
      </BlockStandard>

      <BlockStandard>
        <Button onClick={handleLogout} sx={{ justifyContent: "flex-start" }}>
          <ExitToAppOutlined sx={{ mr: 1 }} color="secondary" />
          <Typography variant="body1">Sair da sua conta</Typography>
        </Button>
      </BlockStandard>

      <Grid item container direction="row" sx={{ flex: 1 }}>
        <DeleteCTA
          buttonLabel="DELETAR CONTA"
          onDeleteConfirmed={handleDelete}
          discreet
        />
      </Grid>

      <Dialog
        open={showPasswordUpdate}
        onClose={() => setShowPasswordUpdate(false)}
      >
        <Grid
          item
          container
          direction="column"
          gap={3}
          padding={4}
          sx={{ minWidth: "400px" }}
        >
          <PasswordText
            value={password}
            onChange={setPassword}
            label="Senha atual"
          />
          <PasswordText
            value={newPassword}
            onChange={setNewPassword}
            label="Nova senha"
            withConfirmation
          />
          <Button variant="contained" onClick={handlePasswordUpdate}>
            Salvar modificações
          </Button>
        </Grid>
      </Dialog>
    </PageStandard>
  );
};
